import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#c9a227] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-400 font-mono text-sm uppercase tracking-wider">Loading...</p>
    </div>
  </div>
);

// Protected Route - requires authentication
export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Redirect to login, saving the attempted URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Free Member Route - requires at least free membership
export const FreeMemberRoute = ({ children }) => {
  const { isAuthenticated, isFreeMember, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isFreeMember) {
    return <Navigate to="/signup" replace />;
  }

  return children;
};

// Paid Member Route - requires paid membership OR admin role
export const PaidMemberRoute = ({ children }) => {
  const { isAuthenticated, isPaidMember, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin users have full Inner Circle access
  if (!isPaidMember && !isAdmin) {
    // Redirect to Inner Circle sales page for upgrade
    return <Navigate to="/inner-circle-sales" state={{ upgrade: true }} replace />;
  }

  return children;
};

// Admin Route - requires admin role
export const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/hub" replace />;
  }

  return children;
};

// Guest Only Route - for login/signup pages (redirect if already logged in)
export const GuestOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    // Redirect to the page they came from, or main menu
    const from = location.state?.from?.pathname || '/hub';
    return <Navigate to={from} replace />;
  }

  return children;
};
